import type { JSX, ReactNode } from 'react';
import cx from 'clsx';

import type { SnippetLang } from './languages';
import { useHighlightedCode } from './useHighlightedCode';

type CodeBlockProps = {
  /** The code to render and highlight. */
  children: string | JSX.Element;
  lang: SnippetLang;
  withLineNumbers?: boolean;
  withCommandLine?: boolean;
  highlightRange?: string;
  highlightOffset?: number;
  /**
   * Rendered inside the `<pre>` after the `<code>`, for chrome that the stylesheet positions
   * over the block — a copy button, typically. Callers that place their controls outside the
   * block (in a header, say) should leave this unset.
   */
  action?: ReactNode;
};

/**
 * The highlighted `<pre><code>` pair, shared by every snippet component.
 *
 * Styling comes from the ancestor: `codeBlock.module.css` reaches these elements through
 * `.snippet pre[class*='language-']` and friends, so render this inside a `CodeBlockContainer`,
 * which owns those classes. This component carries none of its own beyond the ones Prism's
 * plugins look for.
 */
export function CodeBlock({
  children,
  lang,
  withLineNumbers,
  withCommandLine,
  highlightRange,
  highlightOffset,
  action,
}: CodeBlockProps) {
  const codeEl = useHighlightedCode([children, lang]);

  return (
    <pre
      className={cx({ 'line-numbers': withLineNumbers, 'command-line': withCommandLine })}
      data-start={1}
      data-line-offset={highlightOffset ?? ''}
      data-line={highlightRange}
      data-prompt={withCommandLine ? '$' : undefined}
    >
      <code className={`language-${lang}`} ref={codeEl}>
        {children}
      </code>
      {action}
    </pre>
  );
}
